import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatLocalDate } from "../../../../../helper/dateHelpers";

interface ServerDateFilterProps {
  onChange: (lessThan?: string, greaterThan?: string) => void;
}

/**
 * A date filter component for table columns.
 * On the left side is a dropdown with options, on the right side is a datepicker.
 */
const ServerDateFilter = ({ onChange }: ServerDateFilterProps) => {
  const [mode, setMode] = useState<string>("");
  const [dateValue, setDateValue] = useState<Date | null>(null);

  const pillLeftStyle = {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderTopLeftRadius: "50rem",
    borderBottomLeftRadius: "50rem",
  };

  const pillRightStyle = {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: "50rem",
    borderBottomRightRadius: "50rem",
  };

  /**
   * Convert operator to API parameters
   */
  const triggerChange = (newMode: string, newDate: Date | null) => {
    if (!newMode || !newDate) {
      onChange(undefined, undefined);
      return;
    }

    const dateStr = formatLocalDate(newDate)!;

    switch (newMode) {
      case ">":
        onChange(undefined, dateStr);
        break;
      case "<":
        onChange(dateStr, undefined);
        break;
      case "=":
        onChange(dateStr, dateStr);
        break;
      default:
        onChange(undefined, undefined);
    }
  };

  /**
   * Handle date change (both calendar click and manual input)
   */
  const handleDateChange = (date: Date | null) => {
    setDateValue(date);

    // Trigger API call if it's deleted
    if (!date) onChange(undefined, undefined);
  };

  /**
   * Handle mode change
   */
  const handleModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMode = e.target.value;
    setMode(newMode);

    // If there's already a valid date, trigger filter immediately
    if (dateValue) triggerChange(newMode, dateValue);
  };

  // ============================================================
  // CSS
  // ============================================================

  /**
   * add css to calendar of DatePicker
   */
  function styleCalendarPopup() {
    setTimeout(async () => {
      const selectedDates = [
        ".react-datepicker__day--selected",
        ".react-datepicker__day--keyboard-selected",
      ];
      const selectedDatesCSS = {
        backgroundColor: "#cfe2ff", // highlight selected date
        color: "#000", // white text
        borderRadius: "50%", // Circular highlight
      };
      await addStylesToElements(selectedDates, selectedDatesCSS);

      const calendarHeader = [".react-datepicker__header"];
      const calendarHeaderCSS = {
        backgroundColor: "#cfe2ff" /* Highlight header */,
        borderBottom: "1px solid #ddd",
        color: "#fff" /* White text */,
      };
      await addStylesToElements(calendarHeader, calendarHeaderCSS);

      const generelDatePicker = [".react-datepicker"];
      const generelDatePickerCSS = {
        border: "1px solid #ddd" /* Light border */,
        borderRadius: "4px" /* Rounded corners for popup */,
        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)" /* Subtle shadow */,
      };
      await addStylesToElements(generelDatePicker, generelDatePickerCSS);
    }, 0);
  }

  async function addStylesToElements(
    querySelectorStrings: string[],
    styleChanges: Partial<CSSStyleDeclaration>,
  ) {
    for (const querySelectorString of querySelectorStrings) {
      const elements: HTMLElement[] = Array.from(
        document.querySelectorAll(querySelectorString),
      );
      for (const element of elements) {
        if (!element) continue;
        for (const [key, value] of Object.entries(styleChanges)) {
          if (value) {
            element.style[key] = value as string;
          }
        }
      }
    }
    return;
  }

  return (
    <div className="d-flex align-items-center">
      {/* Dropdown for mode */}
      <select
        className="h-100 bg-transparent border-0 fs-7 text-center"
        value={mode}
        onChange={handleModeChange}
        aria-label="Vergleichsoperator"
        style={{
          color: mode === "" ? "#767676" : "#333",
          width: "18px",
          cursor: "pointer",
          ...pillLeftStyle,
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
        }}
      >
        <option value="">{"..."}</option>
        <option value="=">{"="}</option>
        <option value=">">{">"}</option>
        <option value="<">{"<"}</option>
      </select>

      {/* DatePicker */}
      <div
        style={{
          flex: 1,
          position: "relative",
          padding: "0 8px",
          ...pillRightStyle,
        }}
      >
        <DatePicker
          selected={dateValue}
          onChange={handleDateChange}
          onBlur={() => {
            if (mode && dateValue) triggerChange(mode, dateValue);
          }}
          onCalendarOpen={styleCalendarPopup}
          dateFormat="yyyy-MM-dd"
          placeholderText="YYYY-MM-DD"
          isClearable={true}
          className="w-100 h-100 bg-transparent border-0"
          wrapperClassName="w-100 d-block"
          portalId="root"
          popperProps={{
            strategy: "fixed",
          }}
          popperClassName="date-picker-popper-top"
        />
      </div>
    </div>
  );
};

export default ServerDateFilter;
